import React from "react";
import { useTranslation } from "react-i18next";
import { ColorV2 } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import {
  ActionSlot,
  ClearButton,
  FieldWrapper,
  IconWrapper,
  Input,
  LeadingIcon,
  SelectionBadge,
  TooltipAligner,
  TooltipColumn,
  TooltipItem,
  TooltipList,
} from "./searchableDropdown.styles";
import {
  SearchableDropdownSize,
  SearchableDropdownState,
} from "./searchableDropdown.types";
import { splitLabels } from "./searchableDropdown.utils";

export interface SearchableDropdownInputProps {
  label?: string;
  labelId: string;
  listboxId: string;
  placeholderText: string;
  leadingIconName?: string;
  disabled?: boolean;
  size: SearchableDropdownSize;
  visualState: SearchableDropdownState;
  isOpen: boolean;
  highlightedIndex: number;
  resolvedValue: string;
  showExtraCountBadge: boolean;
  multiSelectDisplay: {
    displayLabel: string;
    extraCount: number;
    fullLabel: string;
    labels: string[];
  };
  canClear: boolean;
  fieldRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLInputElement>;
  onOpenDropdown: () => void;
  onCloseDropdown: () => void;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onClear: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleClick: () => void;
}

/** Renders the input field, icons, and selection badge/tooltip. */
export const SearchableDropdownInput = (
  props: SearchableDropdownInputProps,
): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <FieldWrapper
      ref={props.fieldRef}
      $state={props.visualState}
      $size={props.size}
    >
      {props.leadingIconName && (
        <LeadingIcon>
          <CustomIcon
            name={props.leadingIconName}
            size="18px"
            color={ColorV2.Blue5}
          />
        </LeadingIcon>
      )}
      <Input
        ref={props.inputRef}
        type="text"
        value={props.resolvedValue}
        placeholder={props.placeholderText}
        disabled={props.disabled}
        $state={props.visualState}
        $size={props.size}
        onFocus={props.onOpenDropdown}
        onChange={props.onInputChange}
        onKeyDown={props.onKeyDown}
        role="combobox"
        aria-expanded={props.isOpen}
        aria-controls={props.listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          props.isOpen && props.highlightedIndex >= 0
            ? `${props.listboxId}-option-${props.highlightedIndex}`
            : undefined
        }
        aria-labelledby={props.label ? props.labelId : undefined}
        aria-label={props.label ? undefined : props.placeholderText}
      />
      {props.showExtraCountBadge && (
        <TooltipAligner>
          <TooltipHover
            content={
              <TooltipList
                $columns={props.multiSelectDisplay.labels.length > 6 ? 2 : 1}
              >
                {splitLabels(props.multiSelectDisplay.labels).map(
                  (column, columnIndex) => (
                    <TooltipColumn key={`column-${columnIndex}`}>
                      {column.map((label) => (
                        <TooltipItem key={label}>{label}</TooltipItem>
                      ))}
                    </TooltipColumn>
                  ),
                )}
              </TooltipList>
            }
            arrow="down"
            textAlign="left"
            minWidth={230}
            maxWidth={330}
          >
            <SelectionBadge>
              +{props.multiSelectDisplay.extraCount}
            </SelectionBadge>
          </TooltipHover>
        </TooltipAligner>
      )}
      <ActionSlot>
        {props.canClear && (
          <ClearButton
            type="button"
            aria-label={t("General.ClearSelection")}
            onClick={props.onClear}
            disabled={props.disabled}
          >
            <CustomIcon name="v2/cross" size="16px" color={ColorV2.Blue5} />
          </ClearButton>
        )}
        <IconWrapper
          type="button"
          aria-label={t("General.ToggleDropdown")}
          onClick={props.onToggleClick}
          disabled={props.disabled}
        >
          <CustomIcon
            name={props.isOpen ? "v2/up-chevron" : "v2/down-chevron"}
            size="20px"
            color={ColorV2.Blue5}
          />
        </IconWrapper>
      </ActionSlot>
    </FieldWrapper>
  );
};
